import { SHAPES, SLIDERS_DATA } from 'constants/filter.constants';
import { deepClone } from 'helpers/utils';
import isEqual from 'lodash/isEqual';
import mapValues from 'lodash/mapValues';
import xor from 'lodash/xor';
import { AUCTION_TYPES, FE_FILTERS, STONE_TYPE_DATA, TREATED_STONE_DATA } from './constants';

enum TypeToItemType {
  diamonds = 'diamonds',
  jewelry = 'jewelry',
  watches = 'luxury_watches',
}

enum NameToApiName {
  jewelryType = 'main_subset_type_in',
  certificate = 'certificate_in',
  brand = 'brand_in',
  gender = 'gender_in',
  shape = 'shape_in',
  clarity = 'clarity_in',
  color = 'color_in',
  fancy = 'color_in',
  stoneType = '',
  carat = '',
  enhanced = '',
  nonDiamond = '',
  treatedStone = '',
}

export function jsonParseStringValue(value: string | any) {
  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
}

function getJewelryShapes(allShapes: { pretty_name: string }[], visibleShapes: string[]) {
  return allShapes.reduce((all, current) => {
    const indexOfShape = visibleShapes.indexOf(current.pretty_name);
    if (indexOfShape !== -1) {
      const newShapes = [...all];
      const currentShapeOption = current.pretty_name === 'Other' ? { ...current, id: -1 } : current;
      // @ts-ignore
      newShapes[indexOfShape] = currentShapeOption;
      return newShapes;
    }
    return all;
  }, []);
}

export function flattenAdo(ado: any) {
  if (!ado) {
    return undefined;
  }
  const {
    filter_constants: {
      avg_clarity_values: clarityAvg,
      avg_color_values: colorAvg,
      clarity_values: clarity,
      color_values: color,
      filter_options: {
        brands: brandsAll,
        certificates,
        clarity: clarityFilter,
        color: colorFilter,
        genders,
        jewelry_brands: brandsJewelry,
        jewelry_types: typesJewelry,
        shapes: { all: shapesAll, jewelry: shapesJewelry },
      },
    },
  } = ado;

  const shapesAllParsed = jsonParseStringValue(shapesAll);
  const shapesJewelryParsed = jsonParseStringValue(shapesJewelry);

  const filtersData = {
    brandsAll: {
      apiName: 'filter_constants.filter_options.brands',
      value: jsonParseStringValue(brandsAll),
    },
    brandsJewelry: {
      apiName: 'filter_constants.filter_options.jewelry_brands',
      value: jsonParseStringValue(brandsJewelry),
    },
    certificates: {
      apiName: 'filter_constants.filter_options.certificates',
      value: jsonParseStringValue(certificates),
    },
    clarity: {
      apiName: 'filter_constants.clarity_values',
      value: jsonParseStringValue(clarity),
    },
    clarityAvg: {
      apiName: 'filter_constants.avg_clarity_values',
      value: jsonParseStringValue(clarityAvg),
    },
    clarityFilter: {
      apiName: 'filter_constants.filter_options.clarity',
      value: jsonParseStringValue(clarityFilter),
    },
    color: {
      apiName: 'filter_constants.color_values',
      value: jsonParseStringValue(color),
    },
    colorAvg: {
      apiName: 'filter_constants.avg_color_values',
      value: jsonParseStringValue(colorAvg),
    },
    colorFilter: {
      apiName: 'filter_constants.filter_options.color',
      value: jsonParseStringValue(colorFilter),
    },
    genders: {
      apiName: 'filter_constants.filter_options.genders',
      value: jsonParseStringValue(genders),
    },
    shapesAll: {
      apiName: 'filter_constants.filter_options.shapes.all',
      value: shapesAllParsed,
    },
    shapesJewelry: {
      apiName: 'filter_constants.filter_options.shapes.jewelry',
      value:
        shapesJewelryParsed.length > 0
          ? shapesJewelryParsed
          : getJewelryShapes(shapesAllParsed, SHAPES.jewelry),
    },
    typesJewelry: {
      apiName: 'filter_constants.filter_options.jewelry_types',
      value: jsonParseStringValue(typesJewelry),
    },
    stoneType: {
      options: STONE_TYPE_DATA,
    },
    treatedStone: {
      options: TREATED_STONE_DATA,
    },
  };

  return filtersData;
}

function getRangeValue(minLabel: string, maxLabel: string, range: any) {
  let minValue = range.value[minLabel];
  let maxValue = range.value[maxLabel];
  // ado data comes from best to worst
  // filter is worst to best
  // we reverse min/max so it will match ado order
  [minValue, maxValue] = [maxValue, minValue];

  if (!minValue && !maxValue) {
    return [];
  }
  const rangeValueKeys = Object.keys(range.value);
  const selectedLabels = rangeValueKeys.reduce((all: any[], key) => {
    const isClarityKey = SLIDERS_DATA.clarity.includes(key);
    const isColorKey = SLIDERS_DATA.color.includes(key);
    if (
      range.value[key] >= minValue &&
      range.value[key] <= maxValue &&
      (isClarityKey || isColorKey)
    ) {
      return [...all, ...key.split('-')];
    }
    return all;
  }, []);
  const uniqueSelected = Array.from(new Set(selectedLabels));
  return rangeValueKeys.reduce((all: any[], current) => {
    const [first, second] = current.split('-');
    if (uniqueSelected.includes(first) && (!second || uniqueSelected.includes(second))) {
      return [...all, range.value[current]];
    }
    return all;
  }, []);
}

export function getFilterValue(
  type: keyof typeof TypeToItemType,
  filterField: {
    name: AuctionsTypes.FilterNames;
    value: any[];
    data: { [key: number]: any };
    options: any[];
  },
  filtersData: any,
) {
  const { name, value, data } = filterField;
  if (FE_FILTERS.includes(name)) {
    return;
  }
  if (name === 'carat') {
    return {
      carat_weight_from: value[0],
      carat_weight_to: value[1],
    };
  }
  if (name === 'clarity') {
    const { clarity, clarityAvg } = filtersData;
    const { label: minValueLabel } = data[value[0]];
    const { label: maxValueLabel } = data[value[1]];

    const claritySelection = getRangeValue(minValueLabel, maxValueLabel, clarity);
    const clarityAvgSelection = getRangeValue(minValueLabel, maxValueLabel, clarityAvg);

    return {
      clarity_in: [...claritySelection, ...clarityAvgSelection],
    };
  }
  if (name === 'color') {
    const { color, colorAvg } = filtersData;
    const { label: minValueLabel } = data[value[0]];
    const { label: maxValueLabel } = data[value[1]];

    const colorSelection = getRangeValue(minValueLabel, maxValueLabel, color);
    const colorAvgSelection = getRangeValue(minValueLabel, maxValueLabel, colorAvg);

    return {
      color_in: [...colorSelection, ...colorAvgSelection],
    };
  }
  if (name === 'fancy') {
    const { color, colorAvg } = filtersData;

    return {
      color_in: [color.value.Fancy, colorAvg.value.Fancy],
    };
  }
  if (name === 'shape' && value.indexOf(-1) !== -1) {
    const allSHapes = filtersData.shapesAll.value.map((option: { id: number }) => option.id);
    const filterShapes = filterField.options.reduce(
      (all: number[], option: { value: number }) =>
        option.value > 0 ? [...all, option.value] : all,
      [],
    );
    const otherShapes = xor(allSHapes, filterShapes);
    const selectedShapes = value.filter((val) => val > 0);
    return {
      [NameToApiName[name]]: [...otherShapes, ...selectedShapes],
    };
  }

  if (name === 'brand') {
    return type === 'watches' ? { brand_in: value } : { jewelry_brand_in: value };
  }

  return {
    [NameToApiName[name]]: value,
  };
}

export function generateFilters(
  type: keyof typeof TypeToItemType,
  params: any[],
  filtersData: any,
): {
  brand_in?: [];
  gender_in?: [];
  shape_in?: [];
  carat_weight_from?: number;
  carat_weight_to?: number;
  certificate_in?: [];
  clarity_in?: [];
  color_in?: [];
  item_type?: [];
  jewelry_brand_in?: [];
  main_subset_type_in?: [];
} {
  const genericFilters = {
    item_type: TypeToItemType[type],
  };
  const filters = params.reduce((all, current) => {
    if (isEqual(current.initialValue, current.value)) {
      return all;
    }
    return {
      ...all,
      ...getFilterValue(type, current, filtersData),
    };
  }, genericFilters);

  return filters;
}

export function generateFeFilters(
  filters: { name: string; value: any; initialValue: any }[],
): AuctionsTypes.FeFiltersType {
  return filters.reduce((all, current) => {
    if (!FE_FILTERS.includes(current.name) || isEqual(current.value, current.initialValue)) {
      return all;
    }
    return {
      ...all,
      [current.name]: current.value,
    };
  }, {});
}

function generateOptions(values: AuctionsTypes.AdoOptionType[]): AuctionsTypes.OptionType[] {
  return [
    { value: 0, text: 'All' },
    ...values.map((val: AuctionsTypes.AdoOptionType) => ({
      value: val.id,
      text: val.pretty_name,
    })),
  ];
}

export function generateFilterOptions(filtersData?: any): AuctionsTypes.FilterOptions {
  const gender = generateOptions(filtersData.genders.value);
  const certificate = generateOptions(filtersData.certificates.value);
  const shape = generateOptions(filtersData.shapesJewelry.value);
  const stoneType = filtersData.stoneType.options;
  const treatedStone = filtersData.treatedStone.options;

  return {
    diamonds: {
      certificate,
      shape,
      stoneType,
      treatedStone,
    },
    jewelry: {
      certificate,
      shape,
      jewelryType: generateOptions(filtersData.typesJewelry.value),
      brand: generateOptions(filtersData.brandsJewelry.value),
      stoneType,
      treatedStone,
    },
    watches: {
      gender,
      brand: generateOptions(filtersData.brandsAll.value),
    },
  };
}

export function getInitialState<Type extends object>(
  filters: Type,
  filtersData?: any,
): { [key in keyof Type]: AuctionsTypes.FiltersType[] } {
  const filterOptions: AuctionsTypes.FilterOptions = generateFilterOptions(filtersData);

  const keys = Object.keys(filters) as Array<keyof Type>;

  return keys.reduce((allFilters, type) => {
    const currentFilters = filters[type] as unknown as AuctionsTypes.FilterFields[];

    const initialState = currentFilters.reduce((all: AuctionsTypes.FiltersType[], filter) => {
      const name = filter.name as AuctionsTypes.FilterNames;
      // @ts-ignore
      if (filter.type === 'select' && name in filterOptions[type]) {
        // @ts-ignore
        const options = filterOptions[type][name];
        return [
          ...all,
          {
            ...filter,
            options,
            initialValue: [0],
            value: [0],
          },
        ];
      }
      if (filter.type === 'toggle') {
        return [
          ...all,
          {
            ...filter,
            initialValue: false,
            value: false,
          },
        ];
      }
      if (filter.type === 'range' && filter.name !== 'carat' && filter.data) {
        const keys = Object.keys(filter.data);
        const minValue = Number(keys[0]);
        const maxValue = Number(keys[keys.length - 1]);
        const value = [minValue, maxValue];
        return [
          ...all,
          {
            ...filter,
            initialValue: value,
            value: value,
          },
        ];
      }
      if (filter.type === 'range' && filter.name === 'carat') {
        return [
          ...all,
          {
            ...filter,
            initialValue: [0.1, 20],
            value: [0.1, 20],
          },
        ];
      }
      return all;
    }, []);
    return {
      ...allFilters,
      [type]: initialState,
    };
  }, {}) as { [key in keyof Type]: AuctionsTypes.FiltersType[] };
}

export function getSelectNextValue(prevValue: (string | number)[], value: (string | number)[]) {
  const isPrevAllSelected = prevValue.indexOf(0) !== -1;
  const newAllSelected = value.indexOf(0) !== -1;
  if (value.length === 0) {
    return [0];
  }
  if (!isPrevAllSelected && newAllSelected) {
    return [0];
  }
  if (isPrevAllSelected && newAllSelected) {
    return value.filter((val) => val !== 0);
  }
  return value;
}

export function didFilterAuctions(beFilters: {}, feFilters: {}, search: string) {
  return Object.keys({ ...beFilters, ...feFilters }).length > 1 || search !== '';
}

export function didFilterMyAuctions(beFilters: {}, feFilters: {}, search: string) {
  return Object.keys({ ...beFilters, ...feFilters }).length > 0 || search !== '';
}

export function isValidType(type: string): boolean {
  return AUCTION_TYPES.includes(type);
}

export const columnTexts = {
  didnt_win: {
    lead_bid: 'HIGHEST BID',
    end_time: 'ENDED ON',
  },
  won: {
    lead_bid: 'WINNING BID',
  },
  default: {
    item_type: 'TYPE',
    addl: 'ADDL. D',
    id: 'NUMBER',
    certificates: 'CERTIFICATES',
    orig_box: 'BOX & DOCS',
    lead_bid: 'LEAD. BID',
    end_time: 'ENDING',
    min_price_met: 'RESERVE PRICE',
    action: 'TAKE ACTION',
  },
};

const newColumnTexts: { [key: string]: { [key: string]: string } } = {
  didnt_win: {
    ...mapValues(columnTexts.didnt_win, (v) => v.toLowerCase()),
  },
  won: {
    ...mapValues(columnTexts.won, (v) => v.toLowerCase()),
  },
  default: {
    ...mapValues(columnTexts.default, (v) => v.toLowerCase()),
    id: 'Auction No.',
    carat_weight: 'Carat',
    item_type: 'Type',
  },
};

export const getColumnTexts = function (column: string, route: string) {
  let textRoute = newColumnTexts[route],
    textDefault = newColumnTexts['default'];
  return (textRoute && textRoute[column]) || (textDefault && textDefault[column]) || column;
};

// sort array of objects by key
// i think the current solution works for both strings, numbers and nulls
// it should also work for dates..(i think..i hope) e.g "2017-08-23T05:36:58Z"
// and how it also supports Clarit
// @param [Boolean] desc sort order
export const sortByKey = function (hay: any, needle: string, desc: boolean) {
  // we use deepClone for immutability; sort alters original object
  let newHay = deepClone(hay),
    clarity = deepClone(SLIDERS_DATA['clarity']);
  newHay.sort(function (p: { [key: string]: string }, n: { [key: string]: string }) {
    let pk = p[needle] || '',
      nk = n[needle] || ''; // '' works fine with numbers, so
    //      don't worry about type
    if (pk === nk) return 0;

    if (needle === 'carat_weight') {
      try {
        const c1 = parseFloat(pk);
        const c2 = parseFloat(nk);

        return desc ? c1 - c2 : c2 - c1;
      } catch (error) {
        console.error('Failed to convert carat to number');
      }
    }

    if (needle === 'clarity') {
      let pkS = pk.split('-'),
        nkS = nk.split('-'),
        pkI,
        nkI;
      pkI = clarity.indexOf(pkS[0]);
      nkI = clarity.indexOf(nkS[0]);
      // if low range diff: use index cmp, otherwise: stay with str cmp
      // SI1-SI2      # idx: 6
      // I1           # idx: 8
      // 6 < 8        # ST1 < I1
      //
      // VVS1-VVS2    # idx: 2
      // VVS1         # idx: 2
      // VVS1 < VVS1-VVS2 # 2 == 2
      if (pkI !== nkI) {
        pk = pkI;
        nk = nkI;
      }
    }
    // shouldn't be needed, but just in case we have a number dressed
    //    up as a string
    // the damned thing converts '' => 0, not good when dealing with strings,
    // disable the feature for now
    // else if(typeof(pk) !== 'Number' && !isNaN(pk)) {
    //     pk = Number(pk);
    //     nk = Number(nk);
    // }
    // return pk.localeCompare(nk) * (desc ? -1 : 1); // doesn't work with numbers
    return (pk < nk ? -1 : 1) * (desc ? -1 : 1);
  });
  return newHay;
};
